import { AuthenticationService, ConfigurationService, LanguageService, LocaleService, SessionStorageService } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpMethod, HttpResponse } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import { applicationContext, Information, ParameterValue } from '@philips-internal/oswald-ui-foundation/lib/context';

import { ApplicationRoute, AuthenticationRoutesAPI, FoundationRoute } from '../constants';

interface Establishment {
  code: string;
  locale: string;
  name: string;
}

interface Module {
  code: string;
  name: string;
}

interface Parameter {
  module: string;
  values: Record<string, ParameterValue>;
}

interface Profile {
  code: number;
  name: string;
}

interface Sector {
  code: number;
  name: string;
}

interface User {
  code: string;
  establishment: Establishment;
  language: string;
  locale: string;
  name: string;
  photo: string;
  profile: Profile;
  sector: Sector;
  username: string;
  hasDefaultEstablishment: boolean;
}

interface Data {
  establishments: Establishment[];
  modules: Module[];
  parameters: Parameter[];
  profiles: Profile[];
  sectors: Sector[];
  user: User;
}

const defaultModules = [
  { code: 'dashboard', name: 'Dashboard' },
];

function informationMapper(data: HttpResponse<Data>): Information {
  const { body } = data;

  if (!body) {
    throw Error('Missing body.');
  }

  const { establishments, modules, parameters, profiles, sectors, user } = body;
  const allModules = [...defaultModules, ...modules];

  const getModuleParameters = (module: string): Map<string, ParameterValue> => {
    const parameter = parameters.find(
      (parameter: Parameter) => parameter.module === module
    );
    const parametersMap: Map<string, ParameterValue> = new Map();

    if (parameter) {
      for (const [key, value] of Object.entries(parameter.values)) {
        parametersMap.set(key, value);
      }
    }

    return parametersMap;
  };

  if (LanguageService.isLanguageSupported(user.language)) {
    LanguageService.setCurrentLanguage(user.language);
  }
  if (LocaleService.isLocaleSupported(user.locale)) {
    LocaleService.setCurrentLocale(user.locale);
  }

  const hasDefaultEstablishment = establishments.length === 1 || user.hasDefaultEstablishment;
  const establishmentData:any = {
    default: hasDefaultEstablishment,
    customerName: user.establishment.name,
  };
  SessionStorageService.setItem('establishment', establishmentData);

  return {
    establishment: {
      available: establishments.map((establishment: Establishment) => ({
        id: establishment.code,
        locale: establishment.locale,
        name: establishment.name,
        tradingName: establishment.name,
      })),
      current: {
        id: user.establishment.code,
        locale: user.establishment.locale,
        name: user.establishment.name,
        tradingName: user.establishment.name,
      },
    },
    module: {
      available: allModules.map((module: Module) => {
        return {
          id: module.code,
          name: module.name,
          parameters: getModuleParameters(module.code),
        };
      }),
    },
    profile: {
      available: profiles.map((profile: Profile) => ({
        id: profile.code,
        name: profile.name,
      })),
      current: {
        id: user.profile.code,
        name: user.profile.name,
      },
    },
    sector: {
      available: sectors.map((sector: Sector) => ({
        id: sector.code,
        name: sector.name,
      })),
      current: {
        id: user.sector.code,
        name: user.sector.name,
      },
    },
    user: {
      id: user.code,
      image: user.photo,
      language: user.language,
      locale: user.locale,
      name: user.name,
      username: user.username,
    },
  };
}

function setup(): void {
  ConfigurationService.setEndpointMappers({
    // @ts-ignore
    information: informationMapper,
  });
  ConfigurationService.setAuthenticationConfiguration({
    clientId: 'sample',
    endpoints: {
      information: {
        url: AuthenticationRoutesAPI.INFORMATION,
        method: HttpMethod.GET,
      },
      refresh: {
        url: AuthenticationRoutesAPI.REFRESH,
        method: HttpMethod.POST,
      },
      revoke: {
        url: AuthenticationRoutesAPI.REVOKE,
        method: HttpMethod.POST,
      },
    },
    inactivity: {
      time: 15 * 60 * 1000,
    },
    routes: {
      custom: [
        /* Routes that can be accessed without authentication */
        FoundationRoute.ACCOUNT_ACTIVATE,
        FoundationRoute.COOKIES_POLICY,
      ],
      forgotPassword: FoundationRoute.FORGOT_PASSWORD,
      home: ApplicationRoute.DASHBOARD,
      inactivity: FoundationRoute.INACTIVITY,
      login: FoundationRoute.LOGIN,
    },
  });

  AuthenticationService.listenLogout(() => {
    applicationContext.clearNavigationStack();
    applicationContext.closeModules();
    // TODO fix loop tela login, foi preciso remover a linha abaixo
    // AuthenticationService.redirectToLogin();
    localStorage.clear();
  });
}

export { setup };
